// Translation Service with i18n fallback and Bhashini API integration
// This service prioritizes i18n static translations and uses Bhashini API only for dynamic text

import API_CONFIG from '../config/apiConfig';
import i18n from '../i18n';

class TranslationService {
  constructor() {
    // Cache for API translations to avoid repeated calls
    this.cache = new Map();
    // Maximum cache size (to prevent memory issues)
    this.maxCacheSize = 1000;
    // Cache expiration time (24 hours in milliseconds)
    this.cacheExpiration = 24 * 60 * 60 * 1000;
    // Pending requests to avoid duplicate API calls
    this.pendingRequests = new Map();
  }

  /**
   * Generate cache key from text and target language
   */
  getCacheKey(text, targetLang) {
    if (!text || typeof text !== 'string') {
      return `invalid_key_${targetLang}`;
    }
    return `${text.toLowerCase().trim()}_${targetLang}`;
  }

  /**
   * Check if cache entry is still valid
   */
  isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    return Date.now() - cacheEntry.timestamp < this.cacheExpiration;
  }

  /**
   * Clean old cache entries if cache is too large
   */
  cleanCache() {
    if (this.cache.size > this.maxCacheSize) {
      // Remove oldest entries (simple strategy: remove first 20%)
      const entriesToRemove = Math.floor(this.maxCacheSize * 0.2);
      const iterator = this.cache.keys();
      for (let i = 0; i < entriesToRemove; i++) {
        const key = iterator.next().value;
        if (key) this.cache.delete(key);
      }
    }
  }

  /**
   * Translate text using Bhashini API
   * @param {string} text - Text to translate
   * @param {string} sourceLang - Source language code (e.g., 'en')
   * @param {string} targetLang - Target language code (e.g., 'mr')
   * @returns {Promise<string>} - Translated text
   */
  async translateWithAPI(text, sourceLang = 'en', targetLang = 'mr') {
    if (!text || typeof text !== 'string') return text;

    const requestKey = this.getCacheKey(text, targetLang);
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey);
    }

    const cacheKey = this.getCacheKey(text, targetLang);
    const cachedEntry = this.cache.get(cacheKey);
    if (this.isCacheValid(cachedEntry)) {
      return cachedEntry.translation;
    }

    if (!text || !text.trim()) {
      return text;
    }

    if (sourceLang === targetLang) {
      return text;
    }

    const translationPromise = (async () => {
      try {
        const params = new URLSearchParams({
          text: text.trim(),
          sourceLang,
          targetLang,
        });

        const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TRANSLATE}?${params.toString()}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'text/plain; charset=UTF-8',
          },
        });

        if (!response.ok) {
          throw new Error(`Translation API failed with status: ${response.status}`);
        }

        const translatedText = await response.text();

        this.cache.set(cacheKey, {
          translation: translatedText,
          timestamp: Date.now(),
        });
        this.cleanCache();

        return translatedText;
      } catch (error) {
        console.error('Translation API error:', error);
        return text;
      } finally {
        this.pendingRequests.delete(requestKey);
      }
    })();

    this.pendingRequests.set(requestKey, translationPromise);
    return translationPromise;
  }


  /**
   * Translate text with i18n fallback
   * First tries i18n, then falls back to API for dynamic text
   * @param {string} keyOrText - i18n key or plain text
   * @param {object} options - Translation options
   * @returns {string|Promise<string>} - Translated text
   */
  translate(keyOrText, options = {}) {
    const {
      useFallback = true, // Whether to use API fallback
      sourceLang = 'en',
      targetLang = null, // null means use current i18n language
      defaultValue = keyOrText, // Default if translation fails
    } = options;

    const currentLang = targetLang || i18n.language;

    // First, try i18n translation
    const i18nTranslation = i18n.t(keyOrText, { returnObjects: false, defaultValue: null });

    // If i18n translation exists and is different from the key, use it
    if (i18nTranslation && i18nTranslation !== keyOrText) {
      return i18nTranslation;
    }

    // If no i18n translation found and fallback is enabled
    // This means it's dynamic text that needs API translation
    if (useFallback && currentLang !== sourceLang) {
      // Return a promise for async translation (non-blocking)
      return this.translateWithAPI(keyOrText, sourceLang, currentLang).catch(() => {
        // If API fails, return original text
        return defaultValue;
      });
    }

    // Return default value
    return defaultValue;
  }

  /**
   * Translate text synchronously (returns original if async needed)
   * Use this for cases where you need immediate return value
   */
  translateSync(keyOrText, options = {}) {
    const {
      sourceLang = 'en',
      targetLang = null,
      defaultValue = keyOrText,
    } = options;

    const currentLang = targetLang || i18n.language;

    // Try i18n first
    const i18nTranslation = i18n.t(keyOrText, { returnObjects: false, defaultValue: null });

    if (i18nTranslation && i18nTranslation !== keyOrText) {
      return i18nTranslation;
    }

    // Check cache for API translations
    if (currentLang !== sourceLang) {
      const cacheKey = this.getCacheKey(keyOrText, currentLang);
      const cachedEntry = this.cache.get(cacheKey);
      if (this.isCacheValid(cachedEntry)) {
        return cachedEntry.translation;
      }
    }

    // Return default (API translation will be async)
    return defaultValue;
  }

  /**
   * Clear translation cache
   */
  clearCache() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache statistics (for debugging)
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      pendingRequests: this.pendingRequests.size,
    };
  }

  /**
   * Public method to get cached translation synchronously
   * Returns null if not cached
   */
  getCachedTranslation(text, targetLang) {
    if (!text || typeof text !== 'string') return null;
    const cacheKey = this.getCacheKey(text, targetLang);
    const cachedEntry = this.cache.get(cacheKey);
    if (this.isCacheValid(cachedEntry)) {
      return cachedEntry.translation;
    }
    return null;
  }
}

// Export singleton instance
const translationService = new TranslationService();
export default translationService;
